import * as React from "react";
import { uniq, flatten, values } from "lodash";
import { Message } from "semantic-ui-react";

import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { ContextAwareCheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/ContextAwareCheckboxQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { useQuestionnaireContainer } from "@teamprevent/common/questionnaires";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";

import { ConsultationMethodOption, consultationMethodOptions } from "../models";
import { PartialOhCaseDetailModel } from "../../crud/ohCase";
import { EmployeeRecord } from "../../crud/employeeRecord";

interface Props {
    ohCase: PartialOhCaseDetailModel;
    employeeRecord: EmployeeRecord;
}

export const ReferralIntroductionAndDataPrivacySection = (props: Props) => {
    const employeeAddress = props.employeeRecord ?
        [
            props.employeeRecord.addressLine1,
            props.employeeRecord.postCode
        ].filter(x => x).join(" ") :
        null;
    const { readonly, sections } = useQuestionnaireContainer();
    const questions = uniq(flatten(sections.map(section => values(section.questions))));
    const consultationMethodAnswer = getAnswerToQuestionWithId(questions, "IntroductionAndDataPrivacyConsultationMethod");
    return (
        <AccordionQuestionnaireSection
            title="Introduction and Data Privacy"
            key="IntroductionAndDataPrivacy"
            id="IntroductionAndDataPrivacy"
            index={3}
        >
            <p>
                My name is ………………. And I am a …………………….working for Optima Health and on behalf of your employer.
                Is it convenient to talk?
            </p>
            <p>For confidentiality reasons before we proceed I need to just ask you a few security questions?</p>
            <ContextAwareCheckboxQuestion
                hideDivider
                questionConfig={({
                    id: "IntroductionAndDataPrivacyConfirmDOB",
                    // eslint-disable-next-line max-len
                    questionText: `Please confirm your DOB? (${props.ohCase && props.ohCase.dateOfBirth && props.ohCase.dateOfBirth.format("L")})`
                })}
                value="Confirmed"
            />
            <ContextAwareCheckboxQuestion
                hideDivider
                questionConfig={({
                    id: "IntroductionAndDataPrivacyConfirmAddress",
                    // eslint-disable-next-line max-len
                    questionText: `Please could you also confirm the first line of your address and postcode? ${employeeAddress ? `(${employeeAddress})` : ""}`
                })}
                value="Confirmed"
            />
            <ContextAwareCheckboxQuestion
                hideDivider={getAnswerToQuestionWithId(questions, "IntroductionAndDataPrivacyConfirmOtherIdentifier")
                    === "Confirmed"}
                questionConfig={({
                    id: "IntroductionAndDataPrivacyConfirmOtherIdentifier",
                    questionText: "If you are unable to match the DOB with address/postcode, use another identifier"
                })}
                value="Confirmed"
            />
            {getAnswerToQuestionWithId(questions, "IntroductionAndDataPrivacyConfirmOtherIdentifier") === "Confirmed" &&
                <>
                    <TextQuestion
                        inline
                        readonly={readonly}
                        questionConfig={({
                            id: "IntroductionAndDataPrivacyConfirmOtherIdentifierDetails",
                            questionText: "How was identity confirmed"
                        })}
                        multiline
                        includeTextSnippetSelector
                    />
                </>
            }
            <DropdownQuestion
                hideDivider
                inline
                readonly={readonly}
                questionConfig={({ id: "IntroductionAndDataPrivacyConsultationMethod", questionText: "Consultation Method" })}
                options={consultationMethodOptions}
                mandatory
            />
            {
                consultationMethodAnswer === ConsultationMethodOption.Telephone &&
                // We only display this section for legacy forms which have the IntroductionAndDataPrivacyRecordingConsent question
                getAnswerToQuestionWithId(questions, "IntroductionAndDataPrivacyRecordingConsent") && (
                    <>
                        <Message color="red">
                            *If the individual does not want their call to be recorded,
                            explain that the call recording cannot be stopped but we
                            can arrange for it to be deleted within 48 hours.
                            If the employee is happy with this approach then
                            please email <a href="mailto:itsupport@tphealth.co.uk">itsupport@tphealth.co.uk</a>
                            &nbsp;with details of the phone number and time of call with a request for it to be deleted
                        </Message>
                        <ContextAwareCheckboxQuestion
                            preserveInitialQuestionText
                            hideDivider
                            questionConfig={{
                                id: "IntroductionAndDataPrivacyRecordingConsent",
                                questionText: "Thank you. Before I do I need to inform you that Telephone calls at Optima Health "
                                    + "are recorded for training and monitoring purposes. Recordings are only held for a period "
                                    + "of 130 days after which time they are fully deleted. You can request a copy of the "
                                    + "recording or request for it to be deleted by writing to us at our registered address "
                                    + "which can be found on our website. Can you confirm if you are happy to proceed?"
                            }}
                            value="Consented"
                            mandatory
                        />
                    </>
                )}
            {/* If it's a legacy form (i.e. readonly or the above question has been answered)
                then this question used to be conditional so we only render if this question is present */}
            {((readonly === false && getAnswerToQuestionWithId(questions, "IntroductionAndDataPrivacyRecordingConsent") === undefined)
            || getAnswerToQuestionWithId(questions, "IntroductionAndDataPrivacyProceedConsent")) && (
                <ContextAwareCheckboxQuestion
                    preserveInitialQuestionText
                    hideDivider
                    questionConfig={{
                        id: "IntroductionAndDataPrivacyProceedConsent",
                        questionText: "Thank you, we are talking today as your manager has referred you to Occupational Health "
                                + "in order to determine how best to support you. By proceeding with the consultation today you will "
                                + "be providing Optima Health with consent to store and process any personal and health related information "
                                + "that is discussed the consultation. Can you confirm that you are happy to proceed?"
                    }}
                    value="Consented"
                    mandatory
                />
            )}
        </AccordionQuestionnaireSection>);
};
